import { addDays, format, isAfter, isBefore, parseISO } from 'date-fns';

import { Contract } from '@/types/ContractType';

export interface TimelineEvent {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'completed' | 'pending' | 'overdue' | 'in_progress';
  type: 'inspection' | 'financing' | 'title' | 'closing' | 'repair' | 'other';
  assignedTo: 'buyer' | 'seller' | 'agent' | 'all';
  priority: 'high' | 'medium' | 'low';
  completedDate?: string;
  originalDate?: string;
  extensionDays?: number;
  extensionReason?: string;
}

export interface ExtensionOverride {
  milestoneId: string;
  newDate: string;
  extensionDays: number;
  reason: string;
  approvedBy: string;
  approvedAt: string;
}

export interface AddendumOverride {
  milestoneId: string;
  newDate: string;
  addendumId: string;
  addendumType: string;
  reason: string;
}

/**
 * Calculate timeline events from contract data
 */
export const calculateContractTimeline = (
  contract: Contract,
  extensions: ExtensionOverride[] = [],
  addendums: AddendumOverride[] = []
): TimelineEvent[] => {
  if (!contract) return [];

  const now = new Date();
  const ratificationDate = contract.ratifiedAt
    ? parseISO(contract.ratifiedAt)
    : now;

  const events: TimelineEvent[] = [];

  // Earnest money deposits (Wave 4)
  const os = contract.offerSpecifics;
  if (os?.deposit1 != null && Number(os.deposit1) > 0) {
    events.push({
      id: 'deposit1',
      title: 'Earnest money — deposit 1',
      description: `Deposit $${Number(
        os.deposit1
      ).toLocaleString()} to escrow/title.`,
      dueDate: os.deposit1Due || format(ratificationDate, 'yyyy-MM-dd'),
      status: getEventStatus(
        os.deposit1Due ? parseISO(os.deposit1Due) : ratificationDate,
        now
      ),
      type: 'other',
      assignedTo: 'buyer',
      priority: 'high',
    });
  }
  if (os?.deposit2 != null && Number(os.deposit2) > 0) {
    events.push({
      id: 'deposit2',
      title: 'Earnest money — deposit 2',
      description: `Second deposit $${Number(os.deposit2).toLocaleString()}.`,
      dueDate:
        os.deposit2Due || format(addDays(ratificationDate, 7), 'yyyy-MM-dd'),
      status: getEventStatus(
        os.deposit2Due
          ? parseISO(os.deposit2Due)
          : addDays(ratificationDate, 7),
        now
      ),
      type: 'other',
      assignedTo: 'buyer',
      priority: 'medium',
    });
  }
  if (
    contract.commercialTerms?.earnestMoney != null &&
    Number(contract.commercialTerms.earnestMoney) > 0
  ) {
    events.push({
      id: 'commercial_earnest',
      title: 'Commercial earnest money',
      description: `Earnest $${Number(
        contract.commercialTerms.earnestMoney
      ).toLocaleString()}.`,
      dueDate:
        (contract.commercialTerms.closingDate as string | undefined) ||
        os?.closingDate ||
        format(addDays(ratificationDate, 14), 'yyyy-MM-dd'),
      status: getEventStatus(
        contract.commercialTerms.closingDate
          ? parseISO(contract.commercialTerms.closingDate as string)
          : addDays(ratificationDate, 14),
        now
      ),
      type: 'other',
      assignedTo: 'buyer',
      priority: 'high',
    });
  }

  // 1. Inspection Period
  if (
    contract.contingency?.inspectionContingency &&
    // Cast: preserves the existing (enum-value vs. enum-key-name) comparison as-is.
    (contract.contingency.inspectionContingency as string) !== 'NOT_SELECTED' &&
    !contract.thirdPartyInspection?.buyerWaivesInspection
  ) {
    const inspectionDays =
      contract.contingency.homeinspectionExecutionDay || 10;
    const inspectionDeadline = addDays(ratificationDate, inspectionDays);

    // Check for extensions
    const inspectionExtension = extensions.find(
      (ext) => ext.milestoneId === 'inspection'
    );
    const finalInspectionDate = inspectionExtension
      ? parseISO(inspectionExtension.newDate)
      : inspectionDeadline;

    events.push({
      id: 'inspection',
      title: 'Inspection Period Ends',
      description:
        'Buyer to complete all inspections and submit repair requests.',
      dueDate: format(finalInspectionDate, 'yyyy-MM-dd'),
      status: getEventStatus(finalInspectionDate, now),
      type: 'inspection',
      assignedTo: 'buyer',
      priority: 'high',
      originalDate: format(inspectionDeadline, 'yyyy-MM-dd'),
      extensionDays: inspectionExtension?.extensionDays,
      extensionReason: inspectionExtension?.reason,
    });
  }

  // 2. Financing Contingency
  if (
    contract.contingency?.financingContingency &&
    // Cast: preserves the existing (enum-value vs. enum-key-name) comparison as-is.
    (contract.contingency.financingContingency as string) !== 'NOT_SELECTED'
  ) {
    let financingDeadline: Date;

    if (contract.offerSpecifics?.financingCommittmentDate) {
      financingDeadline = parseISO(
        contract.offerSpecifics.financingCommittmentDate
      );
    } else {
      // Default to 21 days from ratification
      financingDeadline = addDays(ratificationDate, 21);
    }

    // Check for extensions
    const financingExtension = extensions.find(
      (ext) => ext.milestoneId === 'financing'
    );
    const finalFinancingDate = financingExtension
      ? parseISO(financingExtension.newDate)
      : financingDeadline;

    events.push({
      id: 'financing',
      title: 'Financing Contingency Deadline',
      description:
        'Buyer to secure loan approval and remove financing contingency.',
      dueDate: format(finalFinancingDate, 'yyyy-MM-dd'),
      status: getEventStatus(finalFinancingDate, now),
      type: 'financing',
      assignedTo: 'buyer',
      priority: 'high',
      originalDate: format(financingDeadline, 'yyyy-MM-dd'),
      extensionDays: financingExtension?.extensionDays,
      extensionReason: financingExtension?.reason,
    });
  }

  // 3. Appraisal Contingency
  if (
    contract.contingency?.appraisalContingency &&
    // Cast: preserves the existing (enum-value vs. enum-key-name) comparison as-is.
    (contract.contingency.appraisalContingency as string) !== 'NOT_SELECTED'
  ) {
    const appraisalDays = contract.contingency.daysFromExecutionDate || 14;
    const appraisalDeadline = addDays(ratificationDate, appraisalDays);

    // Check for extensions
    const appraisalExtension = extensions.find(
      (ext) => ext.milestoneId === 'appraisal'
    );
    const finalAppraisalDate = appraisalExtension
      ? parseISO(appraisalExtension.newDate)
      : appraisalDeadline;

    events.push({
      id: 'appraisal',
      title: 'Appraisal Contingency Deadline',
      description: 'Appraisal to be completed and any negotiations finalized.',
      dueDate: format(finalAppraisalDate, 'yyyy-MM-dd'),
      status: getEventStatus(finalAppraisalDate, now),
      type: 'financing',
      assignedTo: 'buyer',
      priority: 'high',
      originalDate: format(appraisalDeadline, 'yyyy-MM-dd'),
      extensionDays: appraisalExtension?.extensionDays,
      extensionReason: appraisalExtension?.reason,
    });
  }

  // 4. Title Commitment
  if (contract.thirdPartyTitleSettlement?.daysToSelectTitleCompany) {
    const titleDays =
      contract.thirdPartyTitleSettlement.daysToSelectTitleCompany;
    const titleDeadline = addDays(ratificationDate, titleDays);

    // Check for extensions
    const titleExtension = extensions.find(
      (ext) => ext.milestoneId === 'title'
    );
    const finalTitleDate = titleExtension
      ? parseISO(titleExtension.newDate)
      : titleDeadline;

    events.push({
      id: 'title',
      title: 'Title Commitment Deadline',
      description:
        'Title company to provide commitment and resolve any issues.',
      dueDate: format(finalTitleDate, 'yyyy-MM-dd'),
      status: getEventStatus(finalTitleDate, now),
      type: 'title',
      assignedTo: 'agent',
      priority: 'medium',
      originalDate: format(titleDeadline, 'yyyy-MM-dd'),
      extensionDays: titleExtension?.extensionDays,
      extensionReason: titleExtension?.reason,
    });
  }

  // 5. Closing Date
  if (contract.offerSpecifics?.closingDate) {
    const closingDate = parseISO(contract.offerSpecifics.closingDate);

    // Check for extensions
    const closingExtension = extensions.find(
      (ext) => ext.milestoneId === 'closing'
    );
    const finalClosingDate = closingExtension
      ? parseISO(closingExtension.newDate)
      : closingDate;

    events.push({
      id: 'closing',
      title: 'Closing Date',
      description: 'Final settlement and transfer of ownership.',
      dueDate: format(finalClosingDate, 'yyyy-MM-dd'),
      status: getEventStatus(finalClosingDate, now),
      type: 'closing',
      assignedTo: 'all',
      priority: 'high',
      originalDate: format(closingDate, 'yyyy-MM-dd'),
      extensionDays: closingExtension?.extensionDays,
      extensionReason: closingExtension?.reason,
    });
  }

  // Sort events by due date
  return events.sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  );
};

/**
 * Determine event status based on dates
 */
const getEventStatus = (
  dueDate: Date,
  now: Date
): 'completed' | 'pending' | 'overdue' | 'in_progress' => {
  if (isBefore(dueDate, now)) {
    return 'overdue';
  }

  // If due within 3 days, mark as in_progress
  const threeDaysFromNow = addDays(now, 3);
  if (isBefore(dueDate, threeDaysFromNow)) {
    return 'in_progress';
  }

  return 'pending';
};

/**
 * Get timeline progress percentage
 */
export const getTimelineProgress = (events: TimelineEvent[]): number => {
  if (events.length === 0) return 0;

  const completedEvents = events.filter(
    (event) => event.status === 'completed'
  ).length;
  return Math.round((completedEvents / events.length) * 100);
};

/**
 * Get overdue events count
 */
export const getOverdueCount = (events: TimelineEvent[]): number => {
  return events.filter((event) => event.status === 'overdue').length;
};

/**
 * Get upcoming events (due within 7 days)
 */
export const getUpcomingEvents = (events: TimelineEvent[]): TimelineEvent[] => {
  const sevenDaysFromNow = addDays(new Date(), 7);
  return events.filter((event) => {
    const dueDate = parseISO(event.dueDate);
    return isBefore(dueDate, sevenDaysFromNow) && event.status !== 'completed';
  });
};

/**
 * Format date for display
 */
export const formatTimelineDate = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy');
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Get days until deadline
 */
export const getDaysUntilDeadline = (dateString: string): number => {
  try {
    const dueDate = parseISO(dateString);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};
